import { useEffect, useRef } from 'react'

import { socketInstance } from 'lib/socket'
import axiosInterceptorInstance from 'api/client'
import { cache } from 'utils/cache'
import { useAuth } from 'features/auth/store/authStore'
import { useUser } from 'features/auth/store/userStore'
import { subscribeSocketEvents } from 'hooks/useDebouncedSocketListener'

// Auction-lifecycle socket events → invalidate the imperative request cache
// (and refresh buyer seller-by-status / dual-market access). Mounted once,
// alongside SocketQueryInvalidator (which handles the React Query cache).
// Extracted verbatim from the retired CacheContext.
const CacheSocketBridge = () => {
  const { token, id, setDualAccess } = useAuth()
  const { setAuctionSellersByStatus } = useUser()
  const currentUserIdRef = useRef(id)

  useEffect(() => {
    currentUserIdRef.current = id
  }, [id])

  const handleRefresh = (status) => cache.deleteKey(status)

  useEffect(() => {
    const activEvents = [
      'vehicle_expired',
      'vehicle_offer_accept',
      'vehicle_offer_declined',
      'vehicle_back_to_parkingloat',
      'vehicle_launch',
      'upcoming_vehicle_live',
    ]
    const liveEvents = [
      'OfferReceive',
      'vehicle_expired',
      'vehicle_offer_accept',
      'upcoming_vehicle_live',
      'vehicle_move_to_parking',
      'vehicle_back_to_parkingloat',
      'vehicle_offer_declined',
    ]
    const buyerHomeEvents = [
      'vehicle_expired',
      'vehicle_offer_accept',
      'vehicle_back_to_parkingloat',
      'vehicle_launch',
      'upcoming_vehicle_live',
      'vehicle_sold',
    ]
    const buyerAllVehicleEvents = [
      'vehicle_expired',
      'vehicle_offer_accept',
      'vehicle_back_to_parkingloat',
      'vehicle_launch',
      'upcoming_vehicle_live',
      'vehicle_sold',
    ]

    const upcomingEvents = [
      'vehicle_launch',
      'vehicle_expired',
      'upcoming_vehicle_live',
    ]
    const pendingEvents = [
      'vehicle_back_to_parkingloat',
      'vehicle_move_to_parking',
      'vehicle_offer_accept',
      'vehicle_offer_expired',
      'vehicle_deleted',
    ]
    const parkingEvents = [
      'vehicle_move_to_parking',
      'vehicle_back_to_parkingloat',
      'vehicle_launch',
      'vehicle_launch_to_buy',
    ]

    const buyNowBuyerEvents = [
      'vehicle_launch_to_buy',
      'vehicle_expired',
      'vehicle_move_to_parking',
      'vehicle_back_to_parkingloat',
      'vehicle_counter_offer_declined',
      'vehicle_offer_accept',
      'buy_it_now_price_updated',
      'Bought_by_buyer',
      'counter_offer_added',
    ]

    const scheduledHandler = () => handleRefresh('scheduled')
    const activeHandler = () => {
      handleRefresh('active')
      handleRefresh('vehicle-details')
    }
    const liveHandler = () => handleRefresh('live')
    const buyerHomeHandler = () => {
      handleRefresh('active')
      handleRefresh('upcoming')
      handleRefresh('awarded')
    }

    const getAuctionSellersByStatus = async () => {
      try {
        if (!token) return
        const response = await axiosInterceptorInstance.get(
          '/vehicle/buyer/vehicle-title/seller-by-status',
        )

        const groupedData = response?.data?.responses || {}

        const formattedGroupedData = {}

        Object.keys(groupedData).forEach((status) => {
          const options = groupedData[status] || []

          const uniqueOptions = options.filter(
            (opt, index, self) =>
              index === self.findIndex((o) => o.value === opt.value),
          )

          formattedGroupedData[status] = [
            { label: 'All', value: 'all' },
            ...uniqueOptions,
          ]
        })
        setAuctionSellersByStatus(formattedGroupedData)
      } catch (error) {
        console.error('Failed to fetch sellers:', error)
        const result = {
          success: false,
          userOptions: [],
        }
        return result
      }
    }

    const buyerAllVehiclesHandler = () => {
      handleRefresh('vehicle-details')
      getAuctionSellersByStatus()
    }

    const upcomingHandler = () => handleRefresh('upcoming')
    const pendingHandler = () => {
      handleRefresh('pending')
    }
    const parkingHandler = () => {
      handleRefresh('parking')
    }

    const buyNowBuyerHandler = () => {
      handleRefresh('buy-now')
    }

    const soldHandler = () => {
      cache.deleteKey('purchased')
      cache.deleteKey('awarded')
    }

    const dualMarketAccessUpdatedHandler = (e) => {
      const userId = e?.userId
      const hasAccess = e?.dual_market_access

      if (currentUserIdRef.current === userId) {
        setDualAccess(hasAccess)
        handleRefresh('buy-now')
      }
    }

    socketInstance.on('dualMarketAccessUpdated', dualMarketAccessUpdatedHandler)

    // vehicle_launch bursts (bulk launch) are debounced (400ms, max 5s); all other
    // events invalidate immediately.
    const subscribe = (events, handler) =>
      subscribeSocketEvents(events, handler, { delay: 400 })
    const unsubscribers = [
      subscribe(['upcoming_vehicle_live'], scheduledHandler),
      subscribe(activEvents, activeHandler),
      subscribe(liveEvents, liveHandler),
      subscribe(buyerHomeEvents, buyerHomeHandler),
      subscribe(buyerAllVehicleEvents, buyerAllVehiclesHandler),
      subscribe(upcomingEvents, upcomingHandler),
      subscribe(pendingEvents, pendingHandler),
      subscribe(parkingEvents, parkingHandler),
      subscribe(['vehicle_sold'], soldHandler),
      subscribe(buyNowBuyerEvents, buyNowBuyerHandler),
    ]

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe())
      socketInstance.off(
        'dualMarketAccessUpdated',
        dualMarketAccessUpdatedHandler,
      )
    }
  }, [])

  return null
}

export default CacheSocketBridge
